import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { storage } from '../utils/storage';
import { strapiGet } from '@/lib/strapi';
import { useAppStore } from '@/lib/store/useAppStore';
import { clearDb } from '@/lib/db/database';
import i18next, { applyRTL } from '@/lib/i18n';
import type {
  LoginCredentials,
  RegisterCredentials,
  AuthContextType,
  AuthState,
  User,
} from '../types/auth.types';
import type { Restaurant } from '@/lib/queries/useRestaurants';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

async function syncRestaurant(_userId: number): Promise<void> {
  const store = useAppStore.getState();
  try {
    const res = await strapiGet<Restaurant[]>('/api/restaurants/me');
    const list = res.data;
    if (Array.isArray(list) && list.length > 0) {
      const restaurant = list.find((r) => r.onboardingCompleted) ?? list[list.length - 1];
      store.setRestaurantId(restaurant.documentId);
      store.setOnboardingCompleted(restaurant.onboardingCompleted ?? false);
      store.setFeatures({
        sheAnswers: restaurant.featureSheAnswers ?? false,
        customerPage: restaurant.featureCustomerPage ?? false,
        pos: restaurant.featurePOS ?? false,
        posMode: restaurant.posMode ?? 'hybrid',
      });
      const lang = restaurant.languageCode ?? 'en'
      store.setLanguageCode(lang)
      i18next.changeLanguage(lang)
      applyRTL(lang)
    } else {
      store.setRestaurantId(null);
      store.setOnboardingCompleted(false);
      store.setFeatures({ sheAnswers: false, customerPage: false, pos: false, posMode: 'hybrid' });
    }
  } catch {
    // Keep persisted store values on network/auth errors
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = storage.getToken();
        const user = storage.getUser();

        if (token && user) {
          // Verify token is still valid by fetching current user
          try {
            const currentUser = await authService.getCurrentUser();
            storage.setUser(currentUser);
            await syncRestaurant(currentUser.id);
            setState({
              user: currentUser,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch {
            // Token is invalid, clear storage
            storage.clear();
            clearDb().catch(console.error);
            const store = useAppStore.getState();
            store.setRestaurantId(null);
            store.setOnboardingCompleted(false);
            store.setFeatures({ sheAnswers: false, customerPage: false, pos: false, posMode: 'hybrid' });
            setState({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        } else {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch {
        storage.clear();
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const response = await authService.login(credentials);
      storage.setToken(response.jwt);
      storage.setUser(response.user);

      await clearDb();
      await syncRestaurant(response.user.id);

      setState({
        user: response.user,
        token: response.jwt,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed. Please check your credentials.';
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const response = await authService.register(credentials);
      storage.setToken(response.jwt);
      storage.setUser(response.user);

      await clearDb();
      // New user has no restaurant yet
      const store = useAppStore.getState();
      store.setRestaurantId(null);
      store.setOnboardingCompleted(false);

      setState({
        user: response.user,
        token: response.jwt,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Registration failed. Please try again.';
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      throw error;
    }
  };

  const logout = useCallback((): void => {
    storage.clear();
    clearDb().catch(console.error);
    const store = useAppStore.getState();
    store.setRestaurantId(null);
    store.setOnboardingCompleted(false);
    store.setFeatures({ sheAnswers: false, customerPage: false, pos: false, posMode: 'hybrid' });
    store.setLanguageCode('en');
    i18next.changeLanguage('en');
    applyRTL('en');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback((): void => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const updateUser = useCallback((patch: Partial<User>): void => {
    setState((prev) => {
      if (!prev.user) return prev
      const updated = { ...prev.user, ...patch }
      storage.setUser(updated)
      return { ...prev, user: updated }
    })
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    register,
    clearError,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
